import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';

import { getProgram } from '@/utils/posthog';
import { Product } from '@/utils/productHelpers';

/**
 * Extracts the product from the URL pathname and registers the corresponding
 * PostHog program as a super property.
 *
 * Supports routes like:
 * - /activate/:product/...
 * - /coverage/:product/...
 * - /spark/login (→ product = 'spark')
 */
const extractProductFromPath = (pathname: string): Product | undefined => {
  const segments = pathname.split('/').filter(Boolean);

  // /activate/spring-health/:product
  if (segments.length >= 3 && segments[0] === 'activate' && segments[1] === 'spring-health') {
    return segments[2] as Product;
  }

  // /activate/:product or /coverage/:product
  if (segments.length >= 2 && ['activate', 'coverage'].includes(segments[0])) {
    return segments[1] as Product;
  }

  // /spark/login or /spark/login/email
  if (segments.length >= 1 && segments[0] === 'spark') {
    return 'spark' as Product;
  }

  return undefined;
};

const ProgramCapture: React.FC = () => {
  const location = useLocation();
  const posthog = usePostHog();

  useEffect(() => {
    const product = extractProductFromPath(location.pathname);
    if (!product) { return; }

    const program = getProgram(product);
    if (posthog) {
      posthog.register({ program });
    }
  }, [location.pathname, posthog]);

  return null;
};

export default ProgramCapture;
